import type { TeaserListItem, Asset, AuthorData } from '~/@types/cms';
// https://pinia.vuejs.org/core-concepts/#setup-stores
export const useMicroDataStore = defineStore('woom-store-micro-data', () => {
    const appStore = useAppStore();
    const config = useRuntimeConfig();
    const route = useRoute();
    const router = useRouter();

    const { socialLinks, schemaOrganisation } = storeToRefs(appStore);
    const baseUrl = config.public.BASE_URL;

    const getAuthorfromAuthorData = (data?: AuthorData) => {
        if (!data) return undefined;
        return {
            '@type': 'Person',
            name: data.name,
            image: data.image,
        } as const;
    };
    const getImageFromAsset = (asset: Asset) => {
        const url = resolveAssetSrc(asset, '16to9');
        if (!asset || !url) return;
        if (typeof asset !== 'string' && isAssetKontentAi(asset)) {
            return {
                '@type': 'ImageObject',
                '@id': asset.data.src,
                url,
                width: asset.data.width,
                height: asset.data.height,
            } as const;
        }

        return {
            '@type': 'ImageObject',
            '@id': url,
            url,
        } as const;
    };

    router.beforeEach(() => {
        contentType.value = undefined;
        headerData.value = undefined;
        blogPostsData.value = [];
    });

    /**
     * Old model: https://gitlab.woombikes.com/website/woom-lib-model/-/blob/master/src/model/Taxonomies/ContentType.ts
     *
     * New model: https://gitlab.woombikes.com/website/woom-webhooks-kontentai/-/blob/main/src/woom-lib-model/src/model/Taxonomies/ContentType.ts
     */
    type ContentTypes = 'blog_listing' | 'blog_post' | 'press_listing' | 'press_post';
    const contentType = ref<ContentTypes>();
    const dateModified = ref<string>();
    const setBaseData = ({ type, dateModified: _dateModified }: { type?: string; dateModified: string }) => {
        contentType.value = type as ContentTypes | undefined;
        dateModified.value = _dateModified;
    };

    type Header = {
        name: string;
        description?: string;
        datePublished?: string;
        author?: AuthorData;
        asset?: Asset;
    };
    const headerData = ref<Header>();
    const setHeader = (data: Header) => {
        if (headerData.value) return;
        headerData.value = data;
    };

    const blogPostsData = ref<TeaserListItem[]>([]);
    const addBlogPostsData = (data: TeaserListItem[]) => {
        blogPostsData.value = blogPostsData.value.concat(data);
    };

    const email = computed(() => {
        return schemaOrganisation.value?.contactPoint.find((cp) => cp.contactType === 'customer_service')?.email;
    });

    const formatContactType = (type: string) => type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

    const awards = computed(() => {
        const awards = schemaOrganisation.value?.awards ?? '';
        return awards
            .split(',')
            .map((award) => award.trim())
            .filter(Boolean)
            .sort();
    });

    const postalAddress = computed(() => {
        return schemaOrganisation.value?.address?.map(
            ({ streetAddress, addressLocality, addressRegion, postalCode, addressCountry, areaServed }) => ({
                '@type': 'PostalAddress' as const,
                streetAddress,
                addressLocality,
                addressRegion: addressRegion || undefined,
                postalCode,
                addressCountry,
                areaServed: areaServed?.split(',').map((area) => area.trim()),
            }),
        );
    });
    const contactPoint = computed(() => {
        return schemaOrganisation.value?.contactPoint?.map(({ contactType, email, telephone, availableLanguages }) => ({
            '@type': 'ContactPoint' as const,
            contactType: formatContactType(contactType),
            email,
            telephone,
            availableLanguage: availableLanguages?.map(capitalize),
        }));
    });

    useJsonld(() => {
        if (contentType.value !== 'blog_listing' || !headerData.value || !blogPostsData.value.length) return null;
        return {
            '@context': 'https://schema.org',
            '@type': 'Blog',
            name: headerData.value.name,
            description: headerData.value.description,
            datePublished: headerData.value.datePublished,
            blogPost: blogPostsData.value.map((item) => {
                return {
                    '@type': 'BlogPosting',
                    headline: item.title,
                    description: item.subtitle,
                    datePublished: item.date || undefined,
                    author: getAuthorfromAuthorData(item.author),
                    image: getImageFromAsset(item.hero),
                    url: `${baseUrl}${item.url}`,
                };
            }),
        };
    });

    useJsonld(() => {
        if (!contentType.value || !['blog_post', 'press_post'].includes(contentType.value) || !headerData.value) return null;
        const type = contentType.value === 'blog_post' ? 'BlogPosting' : 'NewsArticle';
        const url = `${baseUrl}${route.fullPath}`;
        return {
            '@context': 'https://schema.org',
            '@type': type,
            '@id': url,
            mainEntityOfPage: url,
            headline: headerData.value.name,
            description: headerData.value.description,
            datePublished: headerData.value.datePublished,
            author: getAuthorfromAuthorData(headerData.value.author),
            image: getImageFromAsset(headerData.value.asset),
            url,
        };
    });

    useJsonld(() => {
        if (contentType.value !== 'press_listing' || !headerData.value || !blogPostsData.value.length) return null;
        const url = `${baseUrl}${route.fullPath}`;
        return {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: headerData?.value.name,
            description: headerData?.value?.description,
            url: url,
            hasPart: blogPostsData.value.map((item) => {
                return {
                    '@type': 'NewsArticle',
                    headline: item.title,
                    description: item.subtitle,
                    datePublished: item.date || undefined,
                    url: `${baseUrl}${item.url}`,
                    author: getAuthorfromAuthorData(item.author),
                    image: getImageFromAsset(item.hero),
                    isPartOf: {
                        '@type': 'CollectionPage',
                        url: url,
                    },
                };
            }),
        };
    });

    const socialLinksOnly = computed(() => socialLinks.value.map((link) => link.url));
    useJsonld(() => ({
        '@context': 'https://schema.org',
        '@type': 'Organization',
        url: baseUrl,
        sameAs: socialLinksOnly.value,
        logo: `${baseUrl}/woom-logo.svg`,
        name: schemaOrganisation.value?.name ?? undefined,
        legalName: schemaOrganisation.value?.legalName ?? undefined,
        description: schemaOrganisation.value?.description ?? undefined,
        email: email.value,
        founder: [
            { '@type': 'Person', name: 'Marcus Ihlenfeld' },
            { '@type': 'Person', name: 'Christian Bezdeka' },
        ],
        foundingDate: woomFoundingDateSring,
        foundingLocation: { '@type': 'Place', name: 'Vienna, Austria' },
        slogan: schemaOrganisation.value?.slogan ?? undefined,
        award: awards.value,
        telephone: schemaOrganisation.value?.telephone ?? undefined,
        address: postalAddress.value,
        contactPoint: contactPoint.value,
        vatID: schemaOrganisation.value?.vatID ?? undefined,
    }));
    return { setBaseData, setHeader, addBlogPostsData };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useMicroDataStore, import.meta.hot));
}
